import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { PlanningBoardStateDto } from "@/core/features/planning-boards/planning-board-state.dto";
import { StartPlanningBoardDto } from "@/core/features/planning-boards/start-planning-board.dto";
import { AssignPlanningBoardUserDto } from "@/core/features/planning-boards/assign-planning-board-user.dto";
import { UnassignPlanningBoardUserDto } from "@/core/features/planning-boards/unassign-planning-board-user.dto";
import { AssignPlanningBoardVehicleDto } from "@/core/features/planning-boards/assign-planning-board-vehicle.dto";
import { UnassignPlanningBoardVehicleDto } from "@/core/features/planning-boards/unassign-planning-board-vehicle.dto";
import { UpdatePlanningBoardProjectNoteDto } from "@/core/features/planning-boards/update-planning-board-project-note.dto";
import { UpdatePlanningBoardCommentsDto } from "@/core/features/planning-boards/update-planning-board-comments.dto";
import { RemovePlanningBoardProjectDto } from "@/core/features/planning-boards/remove-planning-board-project.dto";
import { AddPlanningBoardProjectDto } from "@/core/features/planning-boards/add-planning-board-project.dto";
import { ReorderPlanningBoardProjectsDto } from "@/core/features/planning-boards/reorder-planning-board-projects.dto";
import { AssignPlanningBoardVacationDto } from "@/core/features/planning-boards/assign-planning-board-vacation.dto";
import { PlanningBoardAbsenceTypeEnum } from "@/core/features/planning-boards/planning-board-absence-type.enum";
import { UnassignPlanningBoardVacationDto } from "@/core/features/planning-boards/unassign-planning-board-vacation.dto";
import { AssignPlanningBoardLoanUserDto } from "@/core/features/planning-boards/assign-planning-board-loan-user.dto";
import { UnassignPlanningBoardLoanUserDto } from "@/core/features/planning-boards/unassign-planning-board-loan-user.dto";
import { AssignPlanningBoardLoanVehicleDto } from "@/core/features/planning-boards/assign-planning-board-loan-vehicle.dto";
import { UnassignPlanningBoardLoanVehicleDto } from "@/core/features/planning-boards/unassign-planning-board-loan-vehicle.dto";
import { AssignPlanningBoardWorkshopVehicleDto } from "@/core/features/planning-boards/assign-planning-board-workshop-vehicle.dto";
import { UnassignPlanningBoardWorkshopVehicleDto } from "@/core/features/planning-boards/unassign-planning-board-workshop-vehicle.dto";
import { AssignPlanningBoardTrainingDto } from "@/core/features/planning-boards/assign-planning-board-training.dto";
import { UnassignPlanningBoardTrainingDto } from "@/core/features/planning-boards/unassign-planning-board-training.dto";
import { BusinessUnitDto } from "@/core/features/business-units/business-unit.dto";

export class PlanningBoardsResource {
  constructor(protected readonly apiHttpClient: ApiHttpClient) {}

  private readonly requestTransformOptions = { excludeExtraneousValues: true } as const;

  private get resourceUrl(): string {
    return "/api/planning-boards";
  }

  async getAccessibleBusinessUnits(search = ""): Promise<BusinessUnitDto[]> {
    const query = new URLSearchParams({ search }).toString();
    const result = await this.apiHttpClient.request<unknown, BusinessUnitDto[]>(
      HttpRequest.create({
        url: `${this.resourceUrl}/business-units`,
        method: "GET",
        auth: true,
        query,
      })
    );

    return plainToInstance(BusinessUnitDto, result);
  }

  async getActive(businessUnitId: string, weekStartDate: string): Promise<PlanningBoardStateDto | null> {
    const query = new URLSearchParams({ businessUnitId, weekStartDate }).toString();
    const result = await this.apiHttpClient.request<unknown, PlanningBoardStateDto | null>(
      HttpRequest.create({
        url: `${this.resourceUrl}/active`,
        method: "GET",
        auth: true,
        query,
      })
    );

    if (!result) {
      return null;
    }

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async start(businessUnitId: string, weekStartDate: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/start`,
        method: "POST",
        auth: true,
        body: plainToInstance(StartPlanningBoardDto, { businessUnitId, weekStartDate }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignUser(
    planningBoardId: string,
    planningBoardProjectId: string,
    userId: string
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-user`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          AssignPlanningBoardUserDto,
          { planningBoardProjectId, userId },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignUser(
    planningBoardId: string,
    planningBoardProjectId: string,
    userId: string
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-user`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          UnassignPlanningBoardUserDto,
          { planningBoardProjectId, userId },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignVacation(
    planningBoardId: string,
    userId: string,
    type: PlanningBoardAbsenceTypeEnum
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-vacation`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AssignPlanningBoardVacationDto, { userId, type }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignVacation(planningBoardId: string, userId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-vacation`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UnassignPlanningBoardVacationDto, { userId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignLoanUser(planningBoardId: string, userId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-loan-user`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AssignPlanningBoardLoanUserDto, { userId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignLoanUser(planningBoardId: string, userId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-loan-user`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UnassignPlanningBoardLoanUserDto, { userId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignLoanVehicle(planningBoardId: string, vehicleId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-loan-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AssignPlanningBoardLoanVehicleDto, { vehicleId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignLoanVehicle(planningBoardId: string, vehicleId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-loan-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UnassignPlanningBoardLoanVehicleDto, { vehicleId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignWorkshopVehicle(planningBoardId: string, vehicleId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-workshop-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AssignPlanningBoardWorkshopVehicleDto, { vehicleId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignWorkshopVehicle(planningBoardId: string, vehicleId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-workshop-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UnassignPlanningBoardWorkshopVehicleDto, { vehicleId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignTraining(planningBoardId: string, userId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-training`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AssignPlanningBoardTrainingDto, { userId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignTraining(planningBoardId: string, userId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-training`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UnassignPlanningBoardTrainingDto, { userId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async assignVehicle(
    planningBoardId: string,
    planningBoardProjectId: string,
    vehicleId: string,
    updatedById: string
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/assign-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          AssignPlanningBoardVehicleDto,
          { planningBoardProjectId, vehicleId, updatedById },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async unassignVehicle(
    planningBoardId: string,
    planningBoardProjectId: string,
    vehicleId: string,
    updatedById: string
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/unassign-vehicle`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          UnassignPlanningBoardVehicleDto,
          { planningBoardProjectId, vehicleId, updatedById },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async updateProjectNote(
    planningBoardId: string,
    planningBoardProjectId: string,
    notes: string | null
  ): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/project-note`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          UpdatePlanningBoardProjectNoteDto,
          { planningBoardProjectId, notes },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async updateComments(planningBoardId: string, comments: string | null): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/comments`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdatePlanningBoardCommentsDto, { comments }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async removeProject(planningBoardId: string, planningBoardProjectId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/remove-project`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemovePlanningBoardProjectDto, { planningBoardProjectId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async addProject(planningBoardId: string, projectId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/add-project`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(AddPlanningBoardProjectDto, { projectId }, this.requestTransformOptions),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async reorderProjects(planningBoardId: string, planningBoardProjectIds: string[]): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/reorder-projects`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(
          ReorderPlanningBoardProjectsDto,
          { planningBoardProjectIds },
          this.requestTransformOptions
        ),
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async addAllProjects(planningBoardId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/add-all-projects`,
        method: "PATCH",
        auth: true,
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async removeAllProjects(planningBoardId: string): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/remove-all-projects`,
        method: "PATCH",
        auth: true,
      })
    );

    return plainToInstance(PlanningBoardStateDto, result);
  }

  async reset(planningBoardId: string): Promise<void> {
    await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}`,
        method: "DELETE",
        auth: true,
      })
    );
  }

  async updateUserDays(planningBoardId: string, planningBoardProjectId: string, userId: string, daysMask: number) {
    return this.mutate(planningBoardId, "user-assignment-days", { planningBoardProjectId, userId, daysMask });
  }

  async updateAbsenceDays(
    planningBoardId: string,
    userId: string,
    type: PlanningBoardAbsenceTypeEnum,
    daysMask: number
  ) {
    return this.mutate(planningBoardId, "absence-days", { userId, type, daysMask });
  }

  async setLock(planningBoardId: string, isLocked: boolean) {
    return this.mutate(planningBoardId, "lock", { isLocked });
  }

  async setRowLock(planningBoardId: string, planningBoardProjectId: string, isLocked: boolean) {
    return this.mutate(planningBoardId, "row-lock", { planningBoardProjectId, isLocked });
  }

  async duplicate(planningBoardId: string, targetWeekStartDate: string) {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/duplicate`,
        method: "POST",
        auth: true,
        body: { targetWeekStartDate },
      })
    );
    return plainToInstance(PlanningBoardStateDto, result);
  }

  async sendWeekEmails(planningBoardId: string): Promise<{ sent: number; skippedTechnicianIds: string[] }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/send-week-emails`,
        method: "POST",
        auth: true,
      })
    );
  }

  private async mutate(planningBoardId: string, action: string, body: object): Promise<PlanningBoardStateDto> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${planningBoardId}/${action}`,
        method: "PATCH",
        auth: true,
        body,
      })
    );
    return plainToInstance(PlanningBoardStateDto, result);
  }
}
